import type { MutableRefObject } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import {
  RequestChatReadUpdatedEventSchema,
  RequestChatUpdatedEventSchema,
  RequestsUpdatedEventSchema,
} from '@magimp/shared-realtime';
import { fetchRequest } from '../api/requests/index.js';
import {
  requestsKeys,
  evictRowFromListCaches,
  invalidateForCreatedRequest,
  narrowInvalidateForRequest,
  narrowInvalidateUnreadLists,
  invalidateUnreadSummariesContaining,
} from '../features/requests/queries.js';
import { useRequestsStore } from '../features/requests/store.js';
import type { RequestUrlStateOpts } from '../features/requests/useRequestUrlState.js';
import { ApiClientError } from '../api/errors.js';
import type { ToastInput } from '../stores/toast.js';

/**
 * @aiPurpose builds the socket-level handlers for the China request domain
 *   (`requests:updated`, `request-chat:updated`, `request-chat-read:updated`).
 *   Splits out of `RealtimeBootstrap` so the cohesive China cache-coherence +
 *   visibility-probe logic lives next to its tests and the bootstrap effect
 *   remains a thin socket-lifecycle shell.
 * @aiInvariant validation is mandatory — malformed payloads are dropped
 *   silently. The downstream invalidation re-fetches under server-side
 *   row-level visibility, so a non-recipient simply re-receives a 404 and
 *   the cache stays empty.
 */
export interface ChinaRealtimeDeps {
  queryClient: QueryClient;
  pathnameRef: MutableRefObject<string>;
  searchParamsRef: MutableRefObject<URLSearchParams>;
  closeRequestRef: MutableRefObject<(opts?: RequestUrlStateOpts) => void>;
  showToastRef: MutableRefObject<(t: ToastInput) => string>;
  tRef: MutableRefObject<TFunction<'requests'>>;
}

export function createChinaRealtimeHandlers(deps: ChinaRealtimeDeps) {
  const { queryClient, pathnameRef, searchParamsRef, closeRequestRef, showToastRef, tRef } = deps;

  // INVARIANT (#1159 PR5): the seeded logist_cn classifies to
  //   scope='all' and still receives the broadcast even after PR4's
  //   audience trim (which only filters narrow custom roles). The
  //   eviction UX for those actors is the frontend probe below:
  //   on a non-deleted `requests:updated`, we attempt a detail fetch;
  //   if the server now returns 404 (status transitioned into
  //   `LOGIST_HIDDEN_STATUSES`), we treat the row like a delete —
  //   close any open drawer, surface a neutral toast, and let the
  //   list-cache invalidation refetch drop the row from the rendered
  //   list. The toast text never reveals the new status.
  const handleChinaRowVanished = (id: string): void => {
    const ui = useRequestsStore.getState();
    if (ui.selectedId === id) {
      ui.select(null);
      ui.setModal(null);
    }
    if (ui.draftPersistedId === id) {
      ui.closeDraft();
    }
    // INVARIANT (#1159 AC#9): on visible→hidden transition the client
    //   MUST evict the row from the TanStack cache. `invalidateQueries`
    //   above only refetches the list — the stale list cache stays
    //   rendered until the network round-trip lands, and the per-id
    //   detail + chat caches stay forever (a future deep-link could
    //   render a snapshot before its own 404 probe lands). Drop both
    //   shapes explicitly.
    queryClient.removeQueries({ queryKey: requestsKeys.detail(id), exact: true });
    queryClient.removeQueries({ queryKey: requestsKeys.chat(id), exact: true });
    evictRowFromListCaches(queryClient, requestsKeys.all, id);
    // INVARIANT (#1159 PR5): the China requests page is mounted at
    //   `/china-rates` (see `routes.tsx`); the drawer is purely a
    //   function of `?openId=` on that route via `useRequestUrlState`.
    //   The gate MUST match the live route — without it, a logist
    //   getting a 404 probe would close the store but leave the URL
    //   with `?openId=...`, leaving the drawer mounted in an
    //   unavailable-row error state.
    const onRequestsRoute = pathnameRef.current === '/china-rates';
    const urlIdMatches = searchParamsRef.current.get('openId') === id;
    if (onRequestsRoute && urlIdMatches) {
      closeRequestRef.current({ replace: true });
    }
  };
  const probeChinaVisibility = (id: string): void => {
    void queryClient
      .fetchQuery({
        queryKey: requestsKeys.detail(id),
        queryFn: ({ signal }) =>
          // INVARIANT (#1178): the probe shares its cache key with
          //   `useRequest`, which opts into `includeBoxVersions`. The
          //   probe MUST mirror that flag — otherwise a probe response
          //   would overwrite the cached `boxes[].updatedAt` with
          //   `undefined`, and the China logist's regroup edit gate
          //   (`canRegroupBoxItems`) would silently disable.
          // INVARIANT (#1597): the probe MUST also mirror
          //   `includeFunnelLinks` for the same reason — a probe response
          //   omitting the funnel codes would overwrite the cached
          //   `leadPublicCode`/`dealPublicCode` with `undefined` and wipe
          //   the drawer's lead/deal chips (AC-4 cache coherence).
          fetchRequest(id, {
            signal,
            includeItemPackages: true,
            includeBoxes: true,
            includeBoxVersions: true,
            includeFunnelLinks: true,
          }),
        staleTime: 0,
      })
      .catch((err) => {
        if (err instanceof ApiClientError && err.httpStatus === 404) {
          handleChinaRowVanished(id);
          showToastRef.current({ tone: 'info', text: tRef.current('rowGone') });
        }
      });
  };
  const requestsUpdatedHandler = (raw: unknown) => {
    const parsed = RequestsUpdatedEventSchema.safeParse(raw);
    if (!parsed.success) return;
    const id = parsed.data.id;
    if (parsed.data.reason === 'deleted') {
      handleChinaRowVanished(id);
      return;
    }
    if (parsed.data.reason === 'created') {
      invalidateForCreatedRequest(queryClient, id);
      return;
    }
    narrowInvalidateForRequest(queryClient, id, true);
    // WHY (#1159 PR5): only non-created reasons can cause a visibility loss.
    // Fresh `created` rows are handled above and skip this probe to avoid an
    // unnecessary detail fetch on every list-prepend.
    probeChinaVisibility(id);
  };
  const chatUpdatedHandler = (raw: unknown) => {
    const parsed = RequestChatUpdatedEventSchema.safeParse(raw);
    if (!parsed.success) return;
    void queryClient.invalidateQueries({ queryKey: requestsKeys.chat(parsed.data.requestId) });
    // WHY (#570/#1664): a new chat message bumps the unread-summary for
    //   counterparties on the row. The event names ONE requestId, so only
    //   the summary queries whose key id-set contains it are invalidated
    //   (PERF-10/RT-2) — never the whole prefix.
    invalidateUnreadSummariesContaining(queryClient, parsed.data.requestId);
    // WHY (#583): an active `?unread=true` list is a function of the
    //   actor's chat-read-state. A new counterparty message can flip
    //   row membership for that filter, so only the unread=true list
    //   queries are narrowly invalidated — other list caches are not
    //   affected by a chat event.
    narrowInvalidateUnreadLists(queryClient);
  };
  const chatReadUpdatedHandler = (raw: unknown) => {
    const parsed = RequestChatReadUpdatedEventSchema.safeParse(raw);
    if (!parsed.success) return;
    // INVARIANT (#570): `kind` partitions the invalidation work — `read`
    //   may flip the focused chat's `readByCounterparty` booleans, so
    //   chat AND summary refresh; `mark` / `unmark` are personal-state
    //   flips that only touch the unread summary. The wire carries no
    //   user/text data, so all we can do is invalidate by `requestId`.
    if (parsed.data.kind === 'read') {
      void queryClient.invalidateQueries({
        queryKey: requestsKeys.chat(parsed.data.requestId),
      });
    }
    // #1664: id-scoped — only summaries holding this requestId refetch.
    invalidateUnreadSummariesContaining(queryClient, parsed.data.requestId);
    // WHY (#583): every read-state mutation can change `?unread=true`
    //   list membership for the actor (a `read` clears the row from
    //   the filtered list, `mark` adds it, `unmark` removes it).
    //   Only unread=true list queries are narrowly invalidated — other
    //   list caches are not affected by a read-state event.
    narrowInvalidateUnreadLists(queryClient);
  };

  return { requestsUpdatedHandler, chatUpdatedHandler, chatReadUpdatedHandler };
}
